/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Step 2 country-level service — loads countries and sites for country-level
 * assignment, preloads all sites across all countries, loads study owner details,
 * and preselects sites based on user's existing RCM group membership.
 *
 * Migrated from: app/view/app/eos/set-up/step2/eosSetUpStep2.country.service.js
 */

import { eosApiService } from '../api/api.eos'
import { getIsMccEnabled, enrichUsers } from './eos.service'
import type {
  EosConfig,
  EosOptions,
  EosCountry,
  EosCountriesMap,
  EosCountrySite,
  EosUser,
  EosStudyOwner
} from '../../types/eos'

// ---------------------------------------------------------------------------
// Types for internal API responses
// ---------------------------------------------------------------------------

interface UsersApiResponse {
  elements: EosUser[]
}

interface SiteWithCountry extends EosCountrySite {
  country: { countryName: string }
}

// ---------------------------------------------------------------------------
// loadCountryLevelData — loads study owners and countries
// ---------------------------------------------------------------------------

interface CountryLevelDataResult {
  studyOwners: EosStudyOwner[]
  countriesMap: EosCountriesMap
}

/**
 * Loads study owners (users with country-level access) and the list of countries
 * for the selected study. Builds the initial countriesMap.
 *
 * Also sets `options.study` for country-level reference.
 */
export async function loadCountryLevelData(config: EosConfig, options: EosOptions): Promise<CountryLevelDataResult> {
  // Set study reference on options (used by country-level flow)
  ;(options as unknown as Record<string, unknown>).study = {
    isStudy: true,
    uuid: 'study',
    name: config.study!.name,
    groupName: `${config.folder!.id}-${config.study!.name}`
  }

  const [studyOwners, countriesResp] = await Promise.all([
    loadStudyOwnersLight(config),
    eosApiService.getCountriesForStudy(config.folder!.id)
  ])

  const countryNames = countriesResp as string[]

  const countriesMap: EosCountriesMap = {}
  for (const countryName of countryNames) {
    countriesMap[countryName] = {
      isCountry: true,
      isCollapsed: true,
      name: countryName
    }
  }

  return { studyOwners, countriesMap }
}

// ---------------------------------------------------------------------------
// getStudyUserDetails — loads user details for a study owner
// ---------------------------------------------------------------------------

/**
 * Loads detailed user data for a study owner (country-level).
 * Builds the `preselectedSitesMap` from the user's RCM group memberships,
 * which is used to auto-select sites when the user is chosen as study owner.
 *
 * No-op if the user already has preselectedSitesMap populated.
 */
export async function getStudyUserDetails(config: EosConfig, user: EosStudyOwner): Promise<void> {
  if (user.preselectedSitesMap) {
    return
  }

  const resp = (await eosApiService.getUserDetails(
    {
      forStudyAndAllSites: true,
      folderId: config.folder!.id,
      mccEnabled: getIsMccEnabled(config),
      studyName: config.study!.name,
      studyUuid: config.study!.uuid,
      envUuid: config.environment?.uuid ?? undefined
    },
    [user] as unknown as Parameters<typeof eosApiService.getUserDetails>[1]
  )) as { elements: EosUser[] }

  const userData = (resp.elements ?? resp)[0]
  Object.assign(user, userData)

  // Build preselectedSitesMap from RCM group names
  // Group names are formatted as "{folderId}-{sanitizedSiteName}"
  user.preselectedSitesMap = (userData.alreadyPresentInRcmGroups ?? []).reduce<Record<string, string>>(
    (map, groupName) => {
      const dashIndex = groupName.indexOf('-')
      if (dashIndex > -1) {
        const siteName = groupName.substring(dashIndex + 1).trim()
        map[siteName] = siteName
      }
      return map
    },
    {}
  )
}

// ---------------------------------------------------------------------------
// preselectCountryBasedOnUser — auto-select sites based on study owner
// ---------------------------------------------------------------------------

/**
 * Preselects sites in a country based on the study owner's existing RCM group
 * memberships. Sets isSelected/wasSelected on each site and determines
 * whether the entire country should be selected.
 *
 * Mutates country and its sites in-place.
 */
export function preselectCountryBasedOnUser(country: EosCountry, user: EosStudyOwner | null): void {
  if (!country.sites) return

  for (const site of country.sites) {
    if (user?.preselectedSitesMap) {
      const isSelected = !!user.preselectedSitesMap[site.sanitizedName!]
      Object.assign(site, {
        isSelected,
        wasSelected: isSelected
      })
    }
  }

  const allSelected =
    country.sites.length > 0 && country.sites.filter((s) => s.isSelected).length === country.sites.length
  const hasPreselectedSite = country.sites.some((s) => s.isSelected)

  Object.assign(country, {
    hasPreselectedSite,
    isSelected: allSelected,
    wasSelected: allSelected
  })
}

// ---------------------------------------------------------------------------
// preloadAllSites — load all sites across all countries in one request
// ---------------------------------------------------------------------------

/**
 * Loads all sites across all countries in a single API call,
 * then distributes them into their respective countries in the countriesMap.
 *
 * Mutates options.countriesMap in-place.
 */
export async function preloadAllSites(config: EosConfig, options: EosOptions): Promise<void> {
  const isMccEnabled = getIsMccEnabled(config)
  const studyUuid = isMccEnabled && config.environment ? config.environment.uuid : config.study!.uuid
  const folderId = config.folder!.id
  const countries = Object.values(options.countriesMap)
  const countryNames = countries.map((c) => c.name)

  const req = loadSitesInCountries(studyUuid, folderId, isMccEnabled, countryNames)

  // Mark all countries as loading
  for (const country of countries) {
    Object.assign(country, {
      isLoading: true,
      sitesRequest: req
    })
  }

  const sites = await req

  // Distribute sites to their countries
  for (const site of sites) {
    const country = countries.find((c) => c.name === site.belongsTo)
    if (country) {
      if (country.sites) {
        country.sites.push(site)
      } else {
        country.sites = [site]
      }
    }
  }

  // Finalize each country
  for (const country of countries) {
    const countrySites = country.sites ?? []
    Object.assign(country, {
      isLoading: false,
      sitesLoaded: true,
      sites: countrySites,
      displaySites: [...countrySites]
    })
  }
}

// ---------------------------------------------------------------------------
// loadSitesInCountries — fetch sites for multiple countries from API
// ---------------------------------------------------------------------------

/**
 * Fetches sites for multiple countries in a single API call.
 * Normalizes country names and adds metadata to each site.
 */
export async function loadSitesInCountries(
  studyUuid: string,
  folderId: number,
  isMccEnabled: boolean,
  countriesList: string[]
): Promise<EosCountrySite[]> {
  const resp = (await eosApiService.getSitesInCountries(studyUuid, folderId, isMccEnabled, countriesList)) as {
    elements: SiteWithCountry[]
  }

  const rawSites = resp.elements ?? []

  for (const site of rawSites) {
    if (!site.country?.countryName) {
      // Country names on BE are sometimes inconsistent
      site.country = { countryName: 'Undefined' }
    }

    const matchedCountryName =
      countriesList.find((cName) => cName === site.country.countryName) ?? site.country.countryName

    Object.assign(site, {
      belongsTo: matchedCountryName,
      isSite: true
    })
  }

  return rawSites as EosCountrySite[]
}

// ---------------------------------------------------------------------------
// Internal: loadStudyOwnersLight — loads study-level users for owner dropdown
// ---------------------------------------------------------------------------

async function loadStudyOwnersLight(config: EosConfig): Promise<EosStudyOwner[]> {
  const studyUuid = config.study!.uuid
  const isMccEnabled = getIsMccEnabled(config)

  const resp = (await eosApiService.getUsersForStudyLight(studyUuid, {
    folderId: config.folder!.id,
    mccEnabled: isMccEnabled,
    studyName: config.study!.name,
    envUuid: isMccEnabled && config.environment ? config.environment.uuid : undefined
  })) as UsersApiResponse

  return enrichUsers(resp.elements ?? []) as EosStudyOwner[]
}
